// Debug version of Dynamic Product Loader for Design Your Own Cane
// This version includes extensive logging to help identify issues

const DYOCProductLoader = {
  // Product handle mappings based on CSV naming conventions
  productHandles: {
    'wood': [
      'wood-oak',
      'wood-hickory',
      'wood-hickory-with-heartwood',
      'wood-pure-hickory'
    ],
    'finish': [
      'stain-gunstock-brown',
      'stain-medium-brown',
      'stain-dark-brown',
      'stain-crimson',
      'stain-black-dye',
      'stain-tung-oil'
    ],
    'horn-style': [
      'horn-style-inside-cut',
      'horn-style-round-horn', 
      'horn-style-gentlemans',
      'horn-style-birds-head',
      'horn-style-tactical',
      'horn-style-elite'
    ],
    'eyes': [
      'eyes-green-round',
      'eyes-green-lifelike',
      'eyes-orange-lifelike',
      'eyes-pink-lifelike',
      'eyes-red-crystal',
      'eyes-red-lifelike',
      'eyes-whitecrystal',
      'eyes-yellowcrystal',
      'eyes-yellowlifelike'
    ],
    'grips': [
      'grips-top-grips',
      'grips-middle-grips',
      'grips-bottom-grips',
      'grips-on-the-crook-1',
      'grips-top-grips-extended',
      'grips-full-length-grips',
      'grips-full-length-grips-lateral'
    ],
    'sharks': [
      'sharks-teeth-12',
      'sharks-teeth-full-length',
      'sharks-teeth-12-angled-sharks-teeth',
      'sharks-teeth-6-inside-bottom-of-shaft'
    ],
    'rumble': [
      'rumble-strip-12',
      'rumble-strip-double-rumble-strip-12',
      'rumble-strip-24',
      'rumble-strip-double-rumble-strip-24',
      'rumble-strip-full-length-of-shaft'
    ],
    'strikingEdges': [
      'striking-edge-on-shaft',
      'striking-edge-inside-bottom-of-shaft'
    ],
    'addons': [
      'premium-copper-tip',
      'palm-rest',
      'paracord-wrap'
    ]
  },

  // Cache for fetched products
  productCache: {},

  // Debug mode flag
  debugMode: false,

  log(message, data = null) {
    if (this.debugMode) {
      console.log(`[DYOC Loader] ${message}`, data || '');
    }
  },

  error(message, data = null) {
    console.error(`[DYOC Loader Error] ${message}`, data || '');
  },

  // Test if products exist by trying to fetch one
  async testProductAccess() {
    this.log('Testing product access...');
    const testHandle = 'horn-style-inside-cut';
    
    try {
      const response = await fetch(`/products/${testHandle}.js`);
      this.log(`Test fetch response status: ${response.status}`);
      
      if (response.ok) {
        const product = await response.json();
        this.log('Successfully fetched test product:', product);
        return true;
      } else {
        this.error('Test product not found. Products may not be imported yet.');
        return false;
      }
    } catch (error) {
      this.error('Error testing product access:', error);
      return false;
    }
  },

  // Fetch a single product by handle
  async fetchProduct(handle) {
    this.log(`Fetching product: ${handle}`);
    
    // Check cache first
    if (this.productCache[handle]) {
      this.log(`Product found in cache: ${handle}`);
      return this.productCache[handle];
    }

    try {
      const response = await fetch(`/products/${handle}.js`);
      this.log(`Fetch response for ${handle}: ${response.status}`);
      
      if (!response.ok) {
        this.error(`Product not found: ${handle} (${response.status})`);
        return null;
      }
      
      const product = await response.json();
      this.log(`Successfully fetched product ${handle}:`, {
        title: product.title,
        price: product.price,
        handle: product.handle,
        id: product.id
      });
      
      this.productCache[handle] = product;
      return product;
    } catch (error) {
      this.error(`Error fetching product ${handle}:`, error);
      return null;
    }
  },

  // Fetch all products for a given type
  async fetchProductsByType(type) {
    this.log(`Fetching all products for type: ${type}`);
    
    const handles = this.productHandles[type] || [];
    this.log(`Handles to fetch:`, handles);
    
    const products = await Promise.all(
      handles.map(handle => this.fetchProduct(handle))
    );
    
    const results = products.map((product, index) => ({
      handle: handles[index],
      product: product,
      price: product ? Math.floor(product.price / 100) : this.getFallbackPrice(type, index),
      image: product ? product.featured_image : null,
      title: product ? product.title : this.getFallbackTitle(type, index),
      found: product !== null
    }));
    
    this.log(`Results for ${type}:`, results);
    return results;
  },

  // Get fallback price if product not found
  getFallbackPrice(type, index) {
    const fallbackPrices = {
      'wood': [60, 60, 115, 185],
      'finish': [55, 55, 55, 65, 65, 45],
      'horn-style': [25, 30, 30, 30, 30, 30],
      'eyes': [40, 40, 40, 40, 40, 40, 40, 40, 40],
      'grips': [35, 35, 35, 35, 45, 120, 60],
      'sharks': [40, 65, 65, 35],
      'rumble': [40, 60, 45, 65, 60],
      'strikingEdges': [45, 45],
      'addons': [99, 45]
    };
    
    const price = fallbackPrices[type]?.[index] || 0;
    this.log(`Using fallback price for ${type}[${index}]: $${price}`);
    return price;
  },

  // Get fallback title if product not found
  getFallbackTitle(type, index) {
    const fallbackTitles = {
      'wood': ['Oak', 'Hickory', 'Hickory with Heartwood', 'Pure Hickory'],
      'finish': ['Gunstock Brown', 'Medium Brown', 'Dark Brown', 'Crimson', 'Black Dye', 'Tung Oil'],
      'horn-style': ['Inside Cut', 'Round Horn', 'Gentlemans', 'Birds Head', 'Tactical', 'Elite'],
      'eyes': ['Green Round', 'Green Lifelike', 'Orange Lifelike', 'Pink Lifelike', 'Red Crystal', 'Red Lifelike', 'White Crystal', 'Yellow Crystal', 'Yellow Lifelike'],
      'grips': ['Top grips', 'Middle grips', 'Bottom grips', 'On the crook', 'Top grips (extended)', 'Full length grips', 'Full length grips (lateral)'],
      'sharks': ['12"', 'Full Length', '12" Angled', '6" Inside'],
      'rumble': ['12"', 'Double 12"', '24"', 'Double 24"', 'Full Length'],
      'strikingEdges': ['On Shaft', 'Inside Bottom of Shaft'],
      'addons': ['Palm Rest', 'Paracord Wrap']
    };
    
    return fallbackTitles[type]?.[index] || '';
  },

  // Update DOM elements with fetched data
  async updateOptionElements(type, containerSelector) {
    this.log(`Updating DOM elements for ${type} in ${containerSelector}`);
    
    const container = document.querySelector(containerSelector);
    if (!container) {
      this.error(`Container not found: ${containerSelector}`);
      return;
    }

    const products = await this.fetchProductsByType(type);
    
    products.forEach((productData, index) => {
      const optionElement = container.children[index];
      if (!optionElement) {
        this.error(`Option element not found at index ${index}`);
        return;
      }

      this.log(`Updating option ${index} with:`, productData);

      // Update price
      if (optionElement.dataset) {
        optionElement.dataset.price = productData.price;
        this.log(`Updated price data attribute to: $${productData.price}`);
      }

      // Update image if available
      if (productData.image) {
        const imgElement = optionElement.querySelector('img');
        if (imgElement) {
          imgElement.src = productData.image;
          imgElement.alt = productData.title;
          this.log(`Updated image for ${productData.title}`);
        }
      }

      // Update price display
      const priceSpan = optionElement.querySelector('.price-display, .horn-price-dynamic, .grip-price-dynamic, .sharks-price-dynamic');
      if (priceSpan) {
        priceSpan.textContent = `(+$${productData.price})`;
        this.log(`Updated price display to: (+$${productData.price})`);
      }

      // Add visual indicator if product was found
      if (productData.found) {
        optionElement.classList.add('product-loaded');
      } else {
        optionElement.classList.add('product-fallback');
      }
    });
  },

  // Initialize dynamic loading for all option types
  async initializeAllOptions() {
    this.log('=== Starting DYOC Product Loader Initialization ===');
    
    // First test if we can access products
    const canAccessProducts = await this.testProductAccess();
    
    if (!canAccessProducts) {
      this.error('Cannot access products. Please ensure products are imported to Shopify.');
      this.log('Falling back to hardcoded values.');
    }

    const loadPromises = [
      this.updateOptionElements('wood', '.wood-options'),
      this.updateOptionElements('finish', '.finish-options'),
      this.updateOptionElements('horn-style', '.horn-options'),
      this.updateOptionElements('eyes', '.crystal-options'),
      this.updateOptionElements('grips', '.grip-options'),
      this.updateOptionElements('sharks', '.sharks-options'),
      this.updateOptionElements('rumble', '.rumble-options'),
      this.updateOptionElements('strikingEdges', '.strikingEdges-options')
    ];

    try {
      await Promise.all(loadPromises);
      this.log('=== All DYOC product data loaded successfully ===');
    } catch (error) {
      this.error('Error loading product data:', error);
    }

    // Log summary
    const summary = {
      totalProductsCached: Object.keys(this.productCache).length,
      productsFound: Object.values(this.productCache).filter(p => p !== null).length,
      productsMissing: Object.keys(this.productHandles).reduce((acc, type) => 
        acc + this.productHandles[type].length, 0) - Object.keys(this.productCache).length
    };
    
    this.log('Loading Summary:', summary);
  }
};

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    DYOCProductLoader.initializeAllOptions();
  });
} else {
  DYOCProductLoader.initializeAllOptions();
}

// Export for use in other scripts
window.DYOCProductLoader = DYOCProductLoader;